<template>
  <button v-if="!user" :class="loginButtonClass" @click="handleLogin" class="m-2" type="button">
    <font-awesome-icon icon="fa-brands fa-google" /> Google
  </button>
  <button
    v-else-if="user && loginButtonClass !== 'btn btn-outline-light'"
    :class="logoutButtonClass"
    type="button"
    @click="handleLogout">
    <font-awesome-icon icon="fa-solid fa-arrow-right-from-bracket" />
    {{ $t('buttons.logout') }}
  </button>
</template>

<script setup>
import { storeToRefs } from 'pinia';
import { ref, nextTick } from 'vue';
import { useOneTap, idRevoke, decodeCredential } from 'vue3-google-signin';
import { useRouter } from 'vue-router';
import { checkToken, logOut, getGoogleNonce } from '../../api/api';
import { useUserStore } from '../../stores/userStore';

defineProps({
  loginButtonClass: {
    type: String,
    default: 'btn btn-outline-primary',
  },
  logoutButtonClass: {
    type: String,
    default: 'dropdown-item',
  },
});

const userStore = useUserStore();
const { user } = storeToRefs(userStore);
const router = useRouter();
const nonce = ref('');
const { login } = useOneTap({
  disableAutomaticPrompt: true,
  nonce,
  onSuccess: async (response) => {
    try {
      const { credential } = response;
      const result = await checkToken(credential, nonce.value);
      if (result.status !== 200) {
        throw new Error('Server validation failed');
      }
      const decodedCredential = decodeCredential(credential);
      const { user: backendUser, token } = result.data;
      const userData = {
        id: backendUser?.id || decodedCredential.id,
        name: backendUser?.name || decodedCredential.name,
        email: backendUser?.email || decodedCredential.email,
        picture: backendUser?.picture || decodedCredential.picture,
        isGoogleAuth: true,
      };
      userStore.setUser(userData);
      if (token) {
        userStore.setToken(token);
      }
      router.push('/today');
    } catch (error) {
      globalThis.$toast?.showToast(error.message, 'danger');
    }
  },
  onError: () => console.error('Error with One Tap Login'),
});

const handleLogin = async () => {
  try {
    const { data } = await getGoogleNonce();
    nonce.value = data.nonce;
    await nextTick();
    login();
  } catch (error) {
    globalThis.$toast?.showToast(error.message, 'danger');
  }
};

const handleLogout = async () => {
  try {
    if (!user.value.isGoogleAuth) {
      const result = await logOut();
      if (result.status !== 200) {
        throw new Error('Server logout failed');
      }
    } else {
      await new Promise((resolve, reject) => {
        idRevoke(user.value.id, (done) => {
          if (done.error) {
            const errorMessage = typeof done.error === 'string' ? done.error : 'Google revoke failed';
            globalThis.$toast?.showToast(errorMessage, 'danger');
            reject(new Error(errorMessage));
          } else {
            resolve(null);
          }
        });
      });

      const result = await logOut();
      if (result.status !== 200) {
        throw new Error('Server logout failed');
      }
    }
    userStore.setToken(null);
    userStore.logout();
    router.push('/login');
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error during logout';
    globalThis.$toast?.showToast(errorMessage, 'danger');
  }
};

</script>
